import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';
import { FlaskConical, Loader2, CheckCircle2, XCircle, Zap, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const WINDOWS = [
  { value: '1', label: 'Last 1 hour' },
  { value: '6', label: 'Last 6 hours' },
  { value: '24', label: 'Last 24 hours' },
  { value: '72', label: 'Last 3 days' },
  { value: '168', label: 'Last 7 days' },
];

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }); } catch { return iso; }
};
const fmtHeld = (secs) => {
  if (secs == null) return '';
  if (secs < 60) return `${Math.round(secs)}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${Math.round(secs % 60)}s`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
};

const CondRow = ({ c }) => (
  <div className="flex items-center gap-1.5 text-[11px]">
    {c.ok ? <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" /> : <XCircle className="w-3 h-3 text-red-500 shrink-0" />}
    <span className="font-medium">{c.label}</span>
    <span className="text-muted-foreground">{c.operator} {c.expected}</span>
    <span className="text-muted-foreground">— now: <span className={c.matched ? 'text-green-600 dark:text-green-400' : 'text-red-500'}>{c.current}</span></span>
    {c.held_secs != null && <span className="text-muted-foreground">(held {fmtHeld(c.held_secs)})</span>}
    {c.timer_secs > 0 && c.timer_mode === 'pre' && (
      <span className={`text-[10px] ${c.timer_met ? 'text-green-600 dark:text-green-400' : 'text-amber-500'}`}>
        timer {fmtHeld(c.timer_secs)} {c.timer_met == null ? '' : c.timer_met ? '✓' : '✗ not met'}
      </span>
    )}
    {c.timer_secs > 0 && c.timer_mode !== 'pre' && <span className="text-[10px] text-muted-foreground">post-timer {fmtHeld(c.timer_secs)}</span>}
  </div>
);

export const RuleTestPreview = ({ api, form, scopeDevices }) => {
  const [deviceId, setDeviceId] = useState('');
  const [windowHours, setWindowHours] = useState('24');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const effDeviceId = deviceId || scopeDevices[0]?.id || '';

  const run = async () => {
    if (!effDeviceId) { toast.error('Select at least one device on the rule first'); return; }
    setLoading(true);
    try {
      const res = await api.post('/event-rules/test-preview', {
        device_id: effDeviceId,
        event_type: form.event_type,
        config: form.config,
        window_hours: parseInt(windowHours, 10),
      });
      setResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test preview failed');
    } finally {
      setLoading(false);
    }
  };

  const live = result?.live;
  const replay = result?.replay;

  return (
    <div className="rounded-lg border border-dashed border-[#2563eb]/40 bg-[#2563eb]/5 p-3 space-y-2.5" data-testid="rule-test-preview-section">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 mr-auto">
          <FlaskConical className="w-4 h-4 text-[#2563eb]" />
          <span className="text-sm font-medium">Test Rule Preview</span>
          <span className="text-[10px] text-muted-foreground">dry-run of your current (unsaved) settings</span>
        </div>
        <Select value={effDeviceId} onValueChange={setDeviceId}>
          <SelectTrigger className="h-7 text-xs w-[180px]" data-testid="rule-test-preview-device"><SelectValue placeholder="Select device..." /></SelectTrigger>
          <SelectContent>
            {scopeDevices.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={windowHours} onValueChange={setWindowHours}>
          <SelectTrigger className="h-7 text-xs w-[130px]" data-testid="rule-test-preview-window"><SelectValue /></SelectTrigger>
          <SelectContent>
            {WINDOWS.map(w => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" className="h-7 text-xs" onClick={run} disabled={loading || !effDeviceId} data-testid="rule-test-preview-run">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Run Test'}
        </Button>
      </div>

      {result && (
        <div className="space-y-2.5">
          {live?.enabled && (
            <div className="rounded-md bg-background/60 border p-2.5 space-y-1.5" data-testid="rule-test-preview-live">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">Live formula state:</span>
                <Badge className={live.formula_result ? 'bg-green-600 hover:bg-green-600 text-white' : 'bg-red-600 hover:bg-red-600 text-white'} data-testid="rule-test-preview-live-result">
                  {live.formula_result ? 'TRUE' : 'FALSE'}
                </Badge>
                {live.main_timer && (
                  <span className="text-[10px] text-muted-foreground">
                    main timer {fmtHeld(live.main_timer.secs)} ({live.main_timer.mode === 'pre' ? 'already' : 'after trigger'})
                    {live.main_timer.met != null && (live.main_timer.met ? ' ✓ met' : ` ✗ held ${fmtHeld(live.main_timer.held_secs)}`)}
                  </span>
                )}
              </div>
              {live.groups.map((g, gi) => (
                <div key={gi} className="pl-1 space-y-0.5">
                  <div className="flex items-center gap-1.5 text-[11px] font-medium">
                    {gi > 0 && <span className="text-[10px] font-bold text-[#2563eb]">{g.junction || live.logic}</span>}
                    <span>Group {gi + 1}</span>
                    {g.result ? <CheckCircle2 className="w-3 h-3 text-green-500" /> : <XCircle className="w-3 h-3 text-red-500" />}
                  </div>
                  <div className="pl-4 space-y-0.5">
                    {g.conditions.map((c, ci) => <CondRow key={ci} c={c} />)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {replay && (
            <div className="rounded-md bg-background/60 border p-2.5 space-y-1.5" data-testid="rule-test-preview-replay">
              {!replay.supported ? (
                <p className="text-[11px] text-muted-foreground">{replay.message}</p>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-xs font-medium">
                    <Zap className="w-3.5 h-3.5 text-amber-500" />
                    Would have fired <span className={replay.fires.length > 0 ? 'text-amber-500' : ''}>{replay.fires.length}{replay.fires.length >= 50 ? '+' : ''} time{replay.fires.length === 1 ? '' : 's'}</span>
                    <span className="text-[10px] text-muted-foreground font-normal">({replay.points_scanned} telemetry points scanned)</span>
                  </div>
                  {replay.message && <p className="text-[11px] text-muted-foreground">{replay.message}</p>}
                  {replay.fires.length > 0 && (
                    <div className="max-h-40 overflow-y-auto space-y-0.5" data-testid="rule-test-preview-fires">
                      {replay.fires.map((f, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid={`rule-test-fire-${i}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                          <span className="font-medium">{fmtTime(f.time)}</span>
                          {form.event_type === 'overspeed_fixed' && <span className="text-muted-foreground">{f.speed} km/h</span>}
                          {Object.entries(f.values || {}).map(([k, v]) => (
                            <span key={k} className="text-muted-foreground">{k}: {v}</span>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                  {replay.near_misses.length > 0 && (
                    <div className="space-y-0.5 pt-1 border-t" data-testid="rule-test-preview-near-misses">
                      <div className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
                        <AlertTriangle className="w-3 h-3" /> Near misses ({replay.near_misses.length})
                      </div>
                      <div className="max-h-28 overflow-y-auto space-y-0.5">
                        {replay.near_misses.map((n, i) => (
                          <div key={i} className="text-[10px] text-muted-foreground pl-4" data-testid={`rule-test-nearmiss-${i}`}>
                            {fmtTime(n.time)} — {n.reason}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {replay.notes?.length > 0 && (
                    <div className="pt-1 border-t space-y-0.5">
                      {replay.notes.map((n, i) => <p key={i} className="text-[10px] text-muted-foreground/70">• {n}</p>)}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
